/**
 * Shared formatting for the per-type leave balances returned by
 * GET employee/leaves/balance (annual / used / remaining, plus eligibility).
 */

export interface LeaveTypeBalance {
  annual: number; // -1 = no limit
  used: number;
  remaining: number; // -1 = no limit
}

export interface LeaveBalanceData {
  email: string;
  name?: string;
  balances: Record<string, LeaveTypeBalance>;
  eligibility?: Record<string, { eligible: boolean; reason: string }>;
}

const TYPE_ORDER = ["casual", "sick", "exigency", "optional", "compensatory", "unpaid"];

const TYPE_LABELS: Record<string, string> = {
  casual: "Casual",
  sick: "Sick",
  exigency: "Exigency",
  optional: "Optional",
  compensatory: "Comp Off",
  unpaid: "Unpaid",
};

export const leaveTypeLabel = (code: string) => TYPE_LABELS[code] || code;

export const sortedLeaveTypes = (balances: Record<string, unknown> = {}) =>
  Object.keys(balances).sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a);
    const bi = TYPE_ORDER.indexOf(b);
    return (ai === -1 ? TYPE_ORDER.length : ai) - (bi === -1 ? TYPE_ORDER.length : bi);
  });

export const isEligible = (data: LeaveBalanceData, code: string) =>
  data.eligibility?.[code]?.eligible !== false;

/** "4" days left, "No limit", or "Not eligible". */
export const remainingText = (data: LeaveBalanceData, code: string) => {
  const balance = data.balances[code];
  if (!isEligible(data, code)) return "Not eligible";
  return balance.remaining === -1 ? "No limit" : String(balance.remaining);
};

/** "1 / 5" used of total, or "0 used" when there is no limit. */
export const usedOfTotalText = (data: LeaveBalanceData, code: string) => {
  const balance = data.balances[code];
  if (!isEligible(data, code)) return "Not eligible";
  return balance.annual === -1 ? `${balance.used} used` : `${balance.used} / ${balance.annual}`;
};
